import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import { CLIENT_PROJECTS, UK_HERO_IMG } from "@/data/projects";

const services = [
  {
    title: "New Builds",
    blurb:
      "Bespoke residential new-builds — from the first ground survey to handover. We work with your architect or our design partners.",
    deliverables: ["Ground works & foundations", "Frame & superstructure", "MEP & finishing", "Landscaping coordination"],
    // New Build photo (Hampstead buff brick facade) — matches category
    img: CLIENT_PROJECTS.find((p) => p.id === "hampstead-newbuild-buff-facade").img,
  },
  {
    title: "Full Renovations",
    blurb:
      "Whole-home refurbishments that honour heritage while delivering modern performance, light, and flow.",
    deliverables: ["Structural alterations", "Bespoke joinery", "Heritage restoration", "Smart home systems"],
    // Renovation photo (Chiswick finished kitchen fit-out) — matches category
    img: CLIENT_PROJECTS.find((p) => p.id === "chiswick-kitchen-fitout").img,
  },
  {
    title: "Extensions",
    blurb:
      "Side-return, wrap-around, rear and loft extensions designed for how families actually live — and built to last.",
    deliverables: ["Planning & party wall", "Steel & structural", "Glazing & roofing", "Interior integration"],
    // Extension photo (Ealing completed two-storey extension) — matches category
    img: CLIENT_PROJECTS.find((p) => p.id === "ealing-completed-extension").img,
  },
  {
    title: "Design-Build",
    blurb:
      "Design and construction under one contract — fewer hand-offs, fewer surprises, one accountable team.",
    deliverables: ["Concept & feasibility", "Detailed design", "Cost engineering", "Build execution"],
    // Standalone hero image (not in the projects gallery) — neutral cover for the hybrid category
    img: UK_HERO_IMG,
  },
];

const process = [
  ["01", "Consult", "Free 45-minute discovery call, on-site or virtual."],
  ["02", "Design", "Concept, feasibility & detailed design with cost engineering."],
  ["03", "Permits", "Planning, party-wall and building-control coordination."],
  ["04", "Build", "Weekly updates, locked budgets, in-house trades on site."],
  ["05", "Handover", "Walkthrough, snagging, warranty pack & aftercare."],
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Our Services — New Builds, Renovations & Extensions"
        description="Domestic construction services from Prime Build Design LTD: new builds, full renovations, kitchen & rear extensions and design-build projects across London."
        path="/services"
      />
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-6">Our services</div>
            <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              From foundations<br /> to finishing touches.
            </h1>
          </div>
          <p className="lg:col-span-5 text-slate-600 text-lg leading-relaxed">
            Four core disciplines, each delivered with the same in-house teams, transparent pricing,
            and ten-year structural guarantee. Choose what fits — or have us handle the entire
            journey end-to-end.
          </p>
        </div>
      </section>

      {/* Services list */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {services.map((s, i) => (
            <div
              key={s.title}
              data-testid={`service-row-${i}`}
              className={`grid lg:grid-cols-12 gap-10 py-16 border-b border-slate-200 items-center ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="lg:col-span-7 zoom-img">
                <img src={s.img} alt={s.title} className="w-full h-[460px] object-cover" loading="lazy" decoding="async" width="1200" height="800" />
              </div>
              <div className="lg:col-span-5">
                <div className="overline mb-4">Service · 0{i + 1}</div>
                <h2 className="font-display text-4xl font-bold text-slate-900 tracking-tight leading-tight">
                  {s.title}
                </h2>
                <p className="mt-5 text-slate-600 leading-relaxed">{s.blurb}</p>
                <ul className="mt-6 space-y-2">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-center gap-3 text-slate-700">
                      <span className="w-1.5 h-1.5 bg-[#C5A059]" /> {d}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/estimator"
                  className="btn-ghost mt-8"
                  data-testid={`service-estimate-link-${i}`}
                >
                  Estimate this service <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="overline mb-4 text-[#C5A059]">How we work</div>
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
            A five-stage process built to remove surprises.
          </h2>
          <div className="mt-16 grid md:grid-cols-5 gap-px bg-slate-800">
            {process.map(([n, t, d], i) => (
              <div key={n} className="bg-slate-950 p-8" data-testid={`process-step-${i}`}>
                <div className="font-display text-[#C5A059] text-sm font-bold">{n}</div>
                <h3 className="mt-3 font-display text-xl font-semibold">{t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
